import { get } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import RDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { de } from 'date-fns/locale/de';
import { useIntl } from 'react-intl';
import { CFormFeedback, CFormLabel } from '@coreui/react';
import { sub } from 'date-fns';
import type { IFieldComponentProps } from './types';

type TDateInput = Date | string | number;
type TDateRange = [Date | null, Date | null];

/** A single date (string, timestamp, Date) or, with `selectsRange`, the [start, end] range. */
type TDatePickerValue = TDateInput | TDateRange | null | undefined;

interface IDatePickerProps extends IFieldComponentProps<TDatePickerValue> {
  /** The message id of the label. */
  label: string;
  dateFormat?: string;
  showYearPicker?: boolean;
  yearItemNumber?: number;
  isClearable?: boolean;
  maxDate?: Date | null;
  startDate?: TDateInput | null;
  endDate?: TDateInput | null;
  selectsRange?: boolean;
}

/** The date of a single-date field (react-datepicker's `selected`). */
const selectedDateOf = (value: TDatePickerValue): Date | undefined => {
  if (!value || Array.isArray(value)) return undefined;
  return new Date(value);
};

registerLocale('de', de);

const DatePicker = ({
  field,
  form,
  label,
  dateFormat = 'dd.MM.yyyy',
  showYearPicker,
  yearItemNumber,
  isClearable,
  maxDate,
  startDate: _startDate,
  endDate: _endDate,
  selectsRange,
}: IDatePickerProps) => {
  const { name } = field;
  const { touched, errors } = form;
  const { formatMessage: t, locale } = useIntl();
  const error = useMemo(() => get(touched, name) && get(errors, name), [touched, errors, name]);
  const [dateRange, setDateRange] = useState<TDateRange>([
    _startDate ? new Date(_startDate) : null,
    _endDate ? new Date(_endDate) : null,
  ]);
  const [startDate, endDate] = dateRange;

  const subTimezoneOffset = useCallback(
    (date: Date) => sub(date, { minutes: new Date(date).getTimezoneOffset() }),
    [],
  );

  // react-datepicker passes the [start, end] range when it selects a range.
  const handleRangeChange = useCallback(
    (date: TDateRange) => {
      let d = date;
      if (date[0] && date[1]) {
        d = [subTimezoneOffset(date[0]), subTimezoneOffset(date[1])];
      }
      setDateRange(d);
      form.setFieldValue(name, d);
    },
    [form, name, subTimezoneOffset],
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      const d = date ? subTimezoneOffset(date) : undefined;
      form.setFieldValue(name, d);
    },
    [form, name, subTimezoneOffset],
  );

  // react-datepicker types the range and the single date picker apart (selectsRange with its onChange), so each
  // gets its own element; both get the same props as before. popperPlacement: 9 opens the calendar below the middle
  // of the input, 4.6 opened it at its start.
  const commonProps = {
    name,
    dateFormat,
    showYearPicker,
    yearItemNumber,
    isClearable,
    startDate,
    endDate,
    locale,
    autoComplete: 'off',
    maxDate: maxDate ?? undefined,
    showYearDropdown: true,
    showMonthDropdown: true,
    className: 'form-control',
  };

  return (
    <div className="mb-3">
      <CFormLabel htmlFor={name} className="text-primary">
        {t({ id: label })}
      </CFormLabel>
      {selectsRange ? (
        <RDatePicker
          {...commonProps}
          popperPlacement="bottom-start"
          selectsRange
          onChange={handleRangeChange}
        />
      ) : (
        <RDatePicker
          {...commonProps}
          popperPlacement="bottom-start"
          selected={selectedDateOf(field.value)}
          onChange={handleDateChange}
        />
      )}
      {error ? (
        <CFormFeedback invalid className="help-block">
          {errors[name]}
        </CFormFeedback>
      ) : null}
    </div>
  );
};

export default DatePicker;
